import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Fade,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  TableChart as ErpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  PlayArrow as ProcessIcon,
  Download as DownloadIcon,
  History as ReprocessIcon,
  Storage as FirebaseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { uploadToFirebase, processDocument, reprocessDocument, listFirebaseDocuments, FirebaseDocument, fetchErpData, ErpDataSection } from '../services/api';
import { ExtractedData, ComparisonResult, Classification, FieldValue } from '../types';
import ProcessingStatus, { ProcessingStep } from './ProcessingStatus';
import { useLanguage } from '../i18n';
import DocumentPreview from './shared/DocumentPreview';

const initialSteps: ProcessingStep[] = [
  { label: 'Document Agent', description: 'Uploading document and preparing for pipeline processing', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Extracting text and structured fields using Document AI + Gemini', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

const reprocessSteps: ProcessingStep[] = [
  { label: 'Document Agent', description: 'Loading document from Firebase storage and preparing pipeline', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Running OCR and extracting structured fields using Gemini Vision', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

// Helper function to extract string value from field (handles both string and FieldValue types)
const getFieldString = (field: string | FieldValue | undefined): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field.normalized || field.translated || field.english_text || field.original || '';
};

const DocumentUpload: React.FC = () => {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [deliveryNumber, setDeliveryNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [classification, setClassification] = useState<Classification | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // ERP Data state
  const [erpSections, setErpSections] = useState<ErpDataSection[]>([]);
  const [erpFilename, setErpFilename] = useState<string>('');
  const [loadingErp, setLoadingErp] = useState(false);
  const [erpVisible, setErpVisible] = useState(false);
  const [erpError, setErpError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [errorLog, setErrorLog] = useState<string | null>(null);

  // Firebase dropdown state
  const [firebaseDocuments, setFirebaseDocuments] = useState<FirebaseDocument[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [mode, setMode] = useState<'new' | 'existing'>('new');

  // Processing status state
  const [processingSteps, setProcessingSteps] = useState<ProcessingStep[]>(initialSteps);
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isProcessingActive, setIsProcessingActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch documents on mount
  useEffect(() => {
    fetchFirebaseDocuments();
  }, []);

  const fetchFirebaseDocuments = async () => {
    setLoadingDocs(true);
    try {
      const response = await listFirebaseDocuments('daily_fee');
      setFirebaseDocuments(response.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Not blocking — dropdown just won't have items
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleViewErpData = async () => {
    if (erpVisible && erpSections.length > 0) {
      // Toggle off if already showing
      setErpVisible(false);
      return;
    }
    setLoadingErp(true);
    setErpError(null);
    try {
      const response = await fetchErpData('daily_fee');
      setErpSections(response.sections);
      setErpFilename(response.filename);
      setErpVisible(true);
      // Expand all sections by default
      setExpandedSections(new Set(response.sections.map((_, i) => i)));
    } catch (error: any) {
      console.error('Error fetching ERP data:', error);
      setErpError(error.response?.data?.detail || error.message || 'Failed to load ERP data');
    } finally {
      setLoadingErp(false);
    }
  };

  const toggleSection = (index: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Timer effect
  useEffect(() => {
    if (isProcessingActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isProcessingActive]);

  const updateStep = (stepIndex: number, status: ProcessingStep['status'], duration?: number) => {
    setProcessingSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status, duration } : step
    ));
    if (status === 'running') {
      setCurrentStep(stepIndex);
    }
  };

  const resetProcessingState = () => {
    setProcessingSteps(initialSteps);
    setCurrentStep(0);
    setElapsedTime(0);
    setIsProcessingActive(false);
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setSelectedDocId('');
      setMode('new');
    }
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setSelectedDocId('');
      setMode('new');
    }
  };

  const handleSelectExistingDoc = (docId: string) => {
    setSelectedDocId(docId);
    if (docId) {
      setMode('existing');
      setFile(null);
      // Auto-populate delivery number from selected document
      const selectedDoc = firebaseDocuments.find(d => d.document_id === docId);
      if (selectedDoc?.delivery_number) {
        setDeliveryNumber(selectedDoc.delivery_number);
      }
      // Reset previous results
      setExtractedData(null);
      setComparisonResult(null);
      setClassification(null);
      setDocumentId(docId);
      setMessage(null);
      setErrorLog(null);
    } else {
      setMode('new');
      setDocumentId(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file' });
      return;
    }

    // Reset and start processing
    resetProcessingState();
    setIsProcessingActive(true);
    setLoading(true);
    setMessage(null);
    setExtractedData(null);
    setComparisonResult(null);
    setClassification(null);

    // Step 1: Upload & OCR
    const step1Start = Date.now();
    updateStep(0, 'running');

    try {
      console.log('Starting Firebase upload...', { filename: file.name, size: file.size });
      const formData = new FormData();
      formData.append('file', file);
      if (deliveryNumber) {
        formData.append('delivery_number', deliveryNumber);
      }

      console.log('Calling uploadToFirebase API...');
      const response = await uploadToFirebase(formData);
      console.log('Upload response received:', response);
      console.log('Firebase storage path:', (response as any).firebase_storage_path);

      // Check if extraction returned empty data
      const hasExtractedFields = response.extracted_data?.all_fields && Object.keys(response.extracted_data.all_fields).length > 0;
      const hasFieldDetails = response.extracted_data?.field_details && Object.keys(response.extracted_data.field_details).length > 0;

      if (!hasExtractedFields && !hasFieldDetails) {
        console.warn('WARNING: Backend returned empty extracted data.');
      }

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: Field Extraction (completed as part of upload)
      updateStep(1, 'running');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep(1, 'completed', 1);

      setDocumentId(response.document_id);
      const extractedDataWithDefaults = {
        ...response.extracted_data,
        all_fields: response.extracted_data?.all_fields || response.extracted_data?.field_details || {},
        field_details: response.extracted_data?.field_details || response.extracted_data?.all_fields || {},
        detected_language: response.extracted_data?.detected_language || 'unknown',
        total_fields_extracted: response.extracted_data?.total_fields_extracted ||
          Object.keys(response.extracted_data?.all_fields || response.extracted_data?.field_details || {}).length
      };
      setExtractedData(extractedDataWithDefaults);
      setMessage({ type: 'success', text: '✅ Document uploaded to Firebase & extracted successfully' });

      // Refresh the dropdown to include the new document
      fetchFirebaseDocuments();
    } catch (error: any) {
      console.error('Upload error:', error);
      updateStep(0, 'error');
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Upload failed',
      });
    } finally {
      setLoading(false);
      setIsProcessingActive(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedDocId) {
      setMessage({ type: 'error', text: 'Please select a document from the dropdown' });
      return;
    }

    setProcessing(true);
    setIsProcessingActive(true);
    setMessage(null);
    setProcessingSteps(reprocessSteps);
    setElapsedTime(0);

    // Step 1: Load Document
    const step1Start = Date.now();
    updateStep(0, 'running');

    try {
      console.log('Reprocessing document:', selectedDocId);
      const response = await reprocessDocument(selectedDocId);

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: OCR & Extraction (backend already did this)
      updateStep(1, 'running');
      await new Promise(resolve => setTimeout(resolve, 600));
      updateStep(1, 'completed', 1);

      // Step 3: ERP Comparison (backend already did this)
      updateStep(2, 'running');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep(2, 'completed', 1);

      // Step 4: Classification (backend already did this)
      updateStep(3, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(3, 'completed', 1);

      // Set extracted data if returned
      console.log('[REPROCESS] Full API response:', JSON.stringify(response, null, 2).substring(0, 2000));
      console.log('[REPROCESS] extracted_data:', response.extracted_data);
      console.log('[REPROCESS] extracted_data.all_fields keys:', Object.keys(response.extracted_data?.all_fields || {}));
      console.log('[REPROCESS] extracted_data.field_details keys:', Object.keys(response.extracted_data?.field_details || {}));
      console.log('[REPROCESS] comparison_result:', response.comparison_result);
      
      if (response.extracted_data) {
        const extractedDataWithDefaults = {
          ...response.extracted_data,
          all_fields: response.extracted_data?.all_fields || response.extracted_data?.field_details || {},
          field_details: response.extracted_data?.field_details || response.extracted_data?.all_fields || {},
          detected_language: response.extracted_data?.detected_language || 'unknown',
          total_fields_extracted: response.extracted_data?.total_fields_extracted ||
            Object.keys(response.extracted_data?.all_fields || response.extracted_data?.field_details || {}).length
        };
        console.log('[REPROCESS] Setting extractedData with all_fields count:', Object.keys(extractedDataWithDefaults.all_fields).length);
        setExtractedData(extractedDataWithDefaults);
      } else {
        console.warn('[REPROCESS] No extracted_data in response!');
      }

      setComparisonResult(response.comparison_result);
      setClassification(response.classification);
      setDocumentId(selectedDocId);

      if (response.is_exception && response.exception_reasons?.length > 0) {
        const logContent = generateErrorLog(response.exception_reasons, response.comparison_result);
        setErrorLog(logContent);
      } else {
        setErrorLog(null);
      }

      setMessage({
        type: 'success',
        text: '✅ Document reprocessed successfully from Firebase',
      });

      // Refresh dropdown
      fetchFirebaseDocuments();
    } catch (error: any) {
      updateStep(0, 'error');
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Reprocessing failed',
      });
    } finally {
      setProcessing(false);
      setIsProcessingActive(false);
    }
  };

  const handleProcess = async () => {
    if (!documentId) {
      setMessage({ type: 'error', text: 'No document uploaded' });
      return;
    }

    setProcessing(true);
    setIsProcessingActive(true);
    setMessage(null);

    // Step 3: ERP Comparison
    const step3Start = Date.now();
    updateStep(2, 'running');

    try {
      const response = await processDocument(documentId);

      const step3Duration = Math.round((Date.now() - step3Start) / 1000);
      updateStep(2, 'completed', step3Duration);

      // Step 4: Classification
      updateStep(3, 'running');
      await new Promise(resolve => setTimeout(resolve, 300));
      updateStep(3, 'completed', 1);

      setComparisonResult(response.comparison_result);
      setClassification(response.classification);

      if (response.is_exception && response.exception_reasons?.length > 0) {
        const logContent = generateErrorLog(response.exception_reasons, response.comparison_result);
        setErrorLog(logContent);
      } else {
        setErrorLog(null);
      }

      setMessage({
        type: 'success',
        text: 'Document processed successfully',
      });
    } catch (error: any) {
      updateStep(2, 'error');
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Processing failed',
      });
    } finally {
      setProcessing(false);
      setIsProcessingActive(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const generateErrorLog = (exceptionReasons: string[], comparisonResults: any[]) => {
    const timestamp = new Date().toISOString();
    const fileName = file?.name || 'Unknown';

    let log = `========================================\n`;
    log += `KrElixir OCR - Document Processing Log\n`;
    log += `Generated by KR Elixir (https://krelixir.com/)\n`;
    log += `========================================\n\n`;
    log += `Timestamp: ${timestamp}\n`;
    log += `Document: ${fileName}\n`;
    log += `Delivery Number: ${deliveryNumber || 'Not provided'}\n\n`;

    log += `----------------------------------------\n`;
    log += `MISSING FIELDS DETECTED\n`;
    log += `----------------------------------------\n\n`;

    exceptionReasons.forEach((reason, index) => {
      log += `${index + 1}. ${reason}\n`;
    });

    log += `\n----------------------------------------\n`;
    log += `REQUIRED FIELDS FOR STANDARD PROCESSING\n`;
    log += `----------------------------------------\n\n`;
    log += `The following fields are required for standard case classification:\n\n`;
    log += `1. Delivery Number (delivery_number)\n`;
    log += `   - Description: Unique identifier for the delivery/shipment\n`;
    log += `   - Example: "12345", "67890"\n`;
    log += `   - Location in document: Usually at the top or header section\n\n`;
    log += `2. License Plate (license_plate)\n`;
    log += `   - Description: Vehicle registration number\n`;
    log += `   - Example: "ABC1234", "XYZ5678"\n`;
    log += `   - Location in document: Vehicle information section\n\n`;
    log += `3. Arrival Date (arrival_date)\n`;
    log += `   - Description: Date when the vehicle arrived\n`;
    log += `   - Format: DD/MM/YYYY\n`;
    log += `   - Example: "15/12/2025"\n\n`;
    log += `4. Arrival Time (arrival_time)\n`;
    log += `   - Description: Time when the vehicle arrived\n`;
    log += `   - Format: HH:MM\n`;
    log += `   - Example: "10:30"\n\n`;

    log += `----------------------------------------\n`;
    log += `COMPARISON RESULTS\n`;
    log += `----------------------------------------\n\n`;

    if (comparisonResults && comparisonResults.length > 0) {
      comparisonResults.forEach((result: any) => {
        log += `Field: ${result.field}\n`;
        log += `  Document Value: ${result.document_value || 'Not found'}\n`;
        log += `  ERP Value: ${result.erp_value || 'Not available'}\n`;
        log += `  Match: ${result.match ? 'Yes' : 'No'}\n`;
        log += `  Status: ${result.discrepancy_type || 'N/A'}\n\n`;
      });
    } else {
      log += `No comparison results available.\n\n`;
    }

    log += `----------------------------------------\n`;
    log += `RECOMMENDATIONS\n`;
    log += `----------------------------------------\n\n`;
    log += `1. Ensure the document contains all required fields listed above.\n`;
    log += `2. Verify the document is clear and readable for OCR processing.\n`;
    log += `3. Check if the document format matches the expected logbook format.\n`;
    log += `4. If using a delivery number, ensure it exists in the ERP system.\n\n`;

    log += `========================================\n`;
    log += `End of Log\n`;
    log += `========================================\n`;

    return log;
  };

  const downloadErrorLog = () => {
    if (!errorLog) return;

    const blob = new Blob([errorLog], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `krelixir-ocr-log-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'success';
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Firebase Documents Dropdown */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: mode === 'existing' ? 'secondary.main' : 'divider',
          background: mode === 'existing'
            ? `linear-gradient(135deg, ${alpha('#3B82F6', 0.04)} 0%, ${alpha('#8B5CF6', 0.04)} 100%)`
            : 'background.paper',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FirebaseIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {t.docUploadPage.selectFromFirebase}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t.docUploadPage.chooseExistingDoc}
            </Typography>
          </Box>
          <Tooltip title={t.docUploadPage.refreshDocList}>
            <IconButton
              size="small"
              onClick={fetchFirebaseDocuments}
              disabled={loadingDocs}
              sx={{ color: 'text.secondary' }}
            >
              {loadingDocs ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel id="firebase-doc-select-label">
            {loadingDocs ? t.docUploadPage.loadingDocs : t.docUploadPage.selectADocument}
          </InputLabel>
          <Select
            labelId="firebase-doc-select-label"
            id="firebase-doc-select"
            value={selectedDocId}
            label={loadingDocs ? t.docUploadPage.loadingDocs : t.docUploadPage.selectADocument}
            onChange={(e) => handleSelectExistingDoc(e.target.value as string)}
            disabled={loadingDocs || loading || processing}
            sx={{
              borderRadius: 2,
              '& .MuiSelect-select': { py: 1.5 },
            }}
          >
            <MenuItem value="">
              <em>{t.docUploadPage.noneUploadNew}</em>
            </MenuItem>
            {firebaseDocuments.map((doc) => (
              <MenuItem key={doc.document_id} value={doc.document_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <FileIcon sx={{ fontSize: 18, color: doc.has_extracted_data ? 'success.main' : 'text.secondary' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {doc.has_extracted_data ? '✅ ' : ''}{doc.filename}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatDate(doc.uploaded_at)}
                      {doc.delivery_number && ` • DLV: ${doc.delivery_number}`}
                      {!doc.has_extracted_data && ' • ⚠️ No extracted data'}
                    </Typography>
                  </Box>
                  <Chip
                    label={doc.status}
                    size="small"
                    color={getStatusColor(doc.status) as any}
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Reprocess button + Preview for existing document */}
        {mode === 'existing' && selectedDocId && (
          <Fade in>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleReprocess}
                disabled={processing || loading}
                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <ReprocessIcon />}
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)',
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {processing ? t.docUploadPage.reprocessing : t.docUploadPage.reprocessWithERP}
              </Button>
              <DocumentPreview
                firebaseDocId={selectedDocId}
                firebaseFilename={firebaseDocuments.find(d => d.document_id === selectedDocId)?.filename}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 200 }}>
                {t.docUploadPage.reprocessDesc}
              </Typography>
            </Box>
          </Fade>
        )}
      </Paper>

      {/* ERP Data Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: erpVisible ? 'success.main' : 'divider',
          background: erpVisible
            ? `linear-gradient(135deg, ${alpha('#10B981', 0.04)} 0%, ${alpha('#059669', 0.04)} 100%)`
            : 'background.paper',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: erpVisible ? 2 : 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ErpIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Enterprise Resource Planning/Management Information System
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              View structured document extraction data from cloud storage
            </Typography>
          </Box>
          <Button
            variant={erpVisible ? 'outlined' : 'contained'}
            size="small"
            onClick={handleViewErpData}
            disabled={loadingErp}
            startIcon={loadingErp ? <CircularProgress size={16} color="inherit" /> : <ViewIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              ...(erpVisible
                ? {
                  borderColor: '#10B981',
                  color: '#10B981',
                  '&:hover': { borderColor: '#059669', backgroundColor: alpha('#10B981', 0.08) },
                }
                : {
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                }),
            }}
          >
            {loadingErp ? 'Loading...' : erpVisible ? 'Hide ERP Data' : 'View ERP Data'}
          </Button>
        </Box>

        {erpError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {erpError}
          </Alert>
        )}

        {erpVisible && erpSections.length > 0 && (
          <Fade in>
            <Box
              sx={{
                maxHeight: 500,
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha('#10B981', 0.3),
                  borderRadius: 3,
                  '&:hover': { background: alpha('#10B981', 0.5) },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  label={`${erpSections.length} Section${erpSections.length > 1 ? 's' : ''}`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Source: {erpFilename}
                </Typography>
              </Box>

              {erpSections.map((section, sectionIndex) => (
                <Box
                  key={sectionIndex}
                  sx={{
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {/* Section Header */}
                  <Box
                    onClick={() => toggleSection(sectionIndex)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      backgroundColor: alpha('#10B981', 0.08),
                      '&:hover': { backgroundColor: alpha('#10B981', 0.12) },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#10B981' }}>
                        {section.section}
                      </Typography>
                      <Chip
                        label={`${section.data.length} record${section.data.length > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                    <IconButton size="small" sx={{ color: 'text.secondary' }}>
                      {expandedSections.has(sectionIndex) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Section Table */}
                  {expandedSections.has(sectionIndex) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {section.headers.map((header) => (
                              <TableCell key={header} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.data.map((row, rowIndex) => (
                            <TableRow
                              key={rowIndex}
                              sx={{
                                '&:nth-of-type(odd)': { backgroundColor: alpha('#10B981', 0.02) },
                                '&:hover': { backgroundColor: alpha('#10B981', 0.06) },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {section.headers.map((header) => (
                                <TableCell key={header} sx={{ fontSize: '0.8rem' }}>
                                  {header === section.headers[0] ? (
                                    <strong>{row[header] || '—'}</strong>
                                  ) : (
                                    row[header] || '—'
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ))}
            </Box>
          </Fade>
        )}
      </Paper>

      {/* Divider between modes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Divider sx={{ flex: 1 }} />
        <Chip
          label={t.docUploadPage.orUploadNew}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: 'text.secondary',
            borderColor: 'divider',
          }}
        />
        <Divider sx={{ flex: 1 }} />
      </Box>

      {/* Delivery Number Input */}
      <TextField
        label={t.docUploadPage.deliveryNumber}
        placeholder={t.docUploadPage.deliveryNumberPlaceholder}
        value={deliveryNumber}
        onChange={(e) => setDeliveryNumber(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 3 }}
      />

      {/* Drag & Drop Upload Area */}
      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'secondary.main' : file ? 'success.main' : mode === 'existing' ? alpha('#9CA3AF', 0.3) : 'divider',
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
          backgroundColor: dragActive ? alpha('#3B82F6', 0.04) : file ? alpha('#10B981', 0.04) : 'transparent',
          transition: 'all 0.2s ease',
          cursor: mode === 'existing' ? 'default' : 'pointer',
          opacity: mode === 'existing' ? 0.5 : 1,
          pointerEvents: mode === 'existing' ? 'none' : 'auto',
          '&:hover': {
            borderColor: mode === 'existing' ? undefined : 'secondary.main',
            backgroundColor: mode === 'existing' ? undefined : alpha('#3B82F6', 0.04),
          },
        }}
      >
        <input
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="file-upload" style={{ cursor: mode === 'existing' ? 'default' : 'pointer', display: 'block' }}>
          {file ? (
            <Box>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: alpha('#10B981', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <FileIcon sx={{ fontSize: 32, color: '#10B981' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {file.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {formatFileSize(file.size)}
              </Typography>
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={(e) => { e.preventDefault(); setFile(null); }}
                sx={{ color: 'text.secondary' }}
              >
                {t.docUploadPage.removeFile}
              </Button>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  backgroundColor: alpha('#3B82F6', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <UploadIcon sx={{ fontSize: 32, color: '#3B82F6' }} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t.docUploadPage.dropFileHere}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t.docUploadPage.supportedFormats}
              </Typography>
            </Box>
          )}
        </label>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
        {file && <DocumentPreview file={file} compact />}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleUpload}
          disabled={loading || !file || mode === 'existing'}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
          sx={{ flex: 1 }}
        >
          {loading ? t.docUploadPage.uploadingToFirebase : t.docUploadPage.uploadAndExtract}
        </Button>
        {documentId && mode === 'new' && (
          <Button
            variant="contained"
            onClick={handleProcess}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <ProcessIcon />}
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              },
            }}
          >
            {processing ? t.common.processing : t.docUploadPage.processDocument}
          </Button>
        )}
      </Box>

      {/* Processing Status with Agent Steps */}
      <ProcessingStatus
        isProcessing={isProcessingActive}
        currentStep={currentStep}
        steps={processingSteps}
        elapsedTime={elapsedTime}
        estimatedTime={120}
      />

      {/* Status Message */}
      {message && (
        <Alert
          severity={message.type}
          sx={{ mt: 3 }}
          icon={message.type === 'success' ? <SuccessIcon /> : undefined}
        >
          {message.text}
        </Alert>
      )}

      {/* Error Log Download Button */}
      {errorLog && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<DownloadIcon />}
            onClick={downloadErrorLog}
            sx={{
              borderColor: '#FFA500',
              color: '#FFA500',
              '&:hover': {
                borderColor: '#FF8C00',
                backgroundColor: alpha('#FFA500', 0.1),
              },
            }}
          >
            Download Processing Log
          </Button>
          <Typography variant="caption" color="text.secondary">
            Some fields were not found in the document. Download the log for details.
          </Typography>
        </Box>
      )}

      {extractedData && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              📋 Extracted Data
            </Typography>
            {(extractedData.total_fields_extracted || Object.keys(extractedData.all_fields || extractedData.field_details || {}).length > 0) && (
              <Chip
                label={`${extractedData.total_fields_extracted || Object.keys(extractedData.all_fields || extractedData.field_details || {}).length} fields extracted`}
                size="small"
                color="success"
              />
            )}
            {extractedData.detected_language && (
              <Chip
                label={`Language: ${extractedData.detected_language.toUpperCase()}`}
                size="small"
                color="primary"
              />
            )}
            {mode === 'existing' && (
              <Chip
                label="From Firebase"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>

          {/* Display all_fields if available, otherwise fallback to field_details */}
          {(extractedData.all_fields && Object.keys(extractedData.all_fields).length > 0) ||
            (extractedData.field_details && Object.keys(extractedData.field_details).length > 0) ? (
            <TableContainer sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              maxHeight: 500,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: alpha('#3B82F6', 0.3),
                borderRadius: 3,
                '&:hover': { background: alpha('#3B82F6', 0.5) },
              },
            }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a1a2e' : alpha('#3B82F6', 0.1) } }}>
                    <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Language</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Original Text</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>English Text</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Normalized Value</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(extractedData.all_fields || extractedData.field_details || {}).map(([key, fieldValue]) => {
                    const value = fieldValue as FieldValue;
                    const displayValue = value?.normalized || value?.translated || value?.english_text || value?.original || '';
                    const originalText = value?.original_text || value?.original || '';
                    const englishText = value?.english_text || value?.translated || '';
                    const language = value?.detected_language || extractedData.detected_language || 'N/A';

                    return (
                      <React.Fragment key={key}>
                        <TableRow>
                          <TableCell>
                            <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                          </TableCell>
                          <TableCell>
                            <Chip label={language.toUpperCase()} size="small" />
                          </TableCell>
                          <TableCell>
                            {originalText || 'N/A'}
                            {value?.paragraphs && value.paragraphs.length > 0 && (
                              <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #ccc' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Paragraphs:
                                </Typography>
                                {value.paragraphs.map((para: string, idx: number) => (
                                  <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
                                    • {para}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>{englishText || 'N/A'}</TableCell>
                          <TableCell>
                            <strong>{displayValue || 'N/A'}</strong>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const conf = value?.confidence;
                              if (conf == null) return <Chip label="N/A" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
                              const pct = Math.round(conf * 100);
                              const color = conf >= 0.8 ? '#10B981' : conf >= 0.5 ? '#F59E0B' : '#EF4444';
                              return <Chip label={`${pct}%`} size="small" sx={{ backgroundColor: alpha(color, 0.15), color, fontWeight: 700, fontSize: '0.75rem', minWidth: 48 }} />;
                            })()}
                          </TableCell>
                        </TableRow>
                        {/* Display sub_fields if present */}
                        {value?.sub_fields && Object.entries(value.sub_fields).map(([subKey, subValue]) => {
                          const subField = subValue as FieldValue;
                          const subDisplayValue = subField?.normalized || subField?.translated || subField?.english_text || subField?.original || '';
                          return (
                            <TableRow key={`${key}_${subKey}`} sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell sx={{ pl: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                  └ {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={(subField?.detected_language || language).toUpperCase()}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{subField?.original_text || subField?.original || 'N/A'}</TableCell>
                              <TableCell>{subField?.english_text || subField?.translated || 'N/A'}</TableCell>
                              <TableCell>{subDisplayValue || 'N/A'}</TableCell>
                              <TableCell>
                                {(() => {
                                  const conf = subField?.confidence;
                                  if (conf == null) return <Chip label="N/A" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
                                  const pct = Math.round(conf * 100);
                                  const color = conf >= 0.8 ? '#10B981' : conf >= 0.5 ? '#F59E0B' : '#EF4444';
                                  return <Chip label={`${pct}%`} size="small" sx={{ backgroundColor: alpha(color, 0.15), color, fontWeight: 700, fontSize: '0.75rem', minWidth: 48 }} />;
                                })()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Fallback: Display basic fields if all_fields/field_details are empty */
            <Box sx={{ p: 2, backgroundColor: alpha('#3B82F6', 0.05), borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Basic extracted fields:
              </Typography>
              {extractedData.delivery_number && (
                <Typography variant="body2">
                  <strong>Delivery Number:</strong> {getFieldString(extractedData.delivery_number)}
                </Typography>
              )}
              {extractedData.license_plate && (
                <Typography variant="body2">
                  <strong>License Plate:</strong> {getFieldString(extractedData.license_plate)}
                </Typography>
              )}
              {extractedData.arrival_date && (
                <Typography variant="body2">
                  <strong>Arrival Date:</strong> {getFieldString(extractedData.arrival_date)}
                </Typography>
              )}
              {extractedData.arrival_time && (
                <Typography variant="body2">
                  <strong>Arrival Time:</strong> {getFieldString(extractedData.arrival_time)}
                </Typography>
              )}
              {extractedData.carrier && (
                <Typography variant="body2">
                  <strong>Carrier:</strong> {getFieldString(extractedData.carrier)}
                </Typography>
              )}
              {extractedData.driver && (
                <Typography variant="body2">
                  <strong>Driver:</strong> {getFieldString(extractedData.driver)}
                </Typography>
              )}
              {!extractedData.delivery_number && !extractedData.license_plate && !extractedData.arrival_date && (
                <Typography variant="body2" color="text.secondary">
                  No fields extracted. Check the console for debugging information.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {comparisonResult && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              📊 Comparison with ERP Data
            </Typography>
            {(() => {
              const noDataCount = comparisonResult.filter(r => !r.document_value && !r.erp_value).length;
              const withDataResults = comparisonResult.filter(r => r.document_value || r.erp_value);
              const matchCount = withDataResults.filter(r => r.match).length;
              const totalCount = withDataResults.length;
              const matchPct = totalCount > 0 ? Math.round((matchCount / totalCount) * 100) : 0;
              return (
                <>
                  <Chip
                    label={`${matchCount}/${totalCount} fields match`}
                    size="small"
                    color={matchPct === 100 ? 'success' : matchPct >= 50 ? 'warning' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${matchPct}% match rate`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      color: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      fontWeight: 600,
                    }}
                  />
                </>
              );
            })()}
          </Box>
          <TableContainer sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>📄 Document Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>🏢 ERP Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%', textAlign: 'center' }}>Match Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%' }}>Discrepancy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResult.map((result) => {
                  const bothEmpty = !result.document_value && !result.erp_value;
                  const isMatch = result.match && !bothEmpty;
                  const isMismatch = !result.match && !bothEmpty;
                  const isNoData = bothEmpty;

                  return (
                  <TableRow
                    key={result.field}
                    sx={{
                      backgroundColor: isNoData
                        ? alpha('#6B7280', 0.03)
                        : isMatch
                          ? alpha('#10B981', 0.03)
                          : alpha('#EF4444', 0.05),
                      '&:hover': {
                        backgroundColor: isNoData
                          ? alpha('#6B7280', 0.08)
                          : isMatch
                            ? alpha('#10B981', 0.08)
                            : alpha('#EF4444', 0.1),
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <strong>{result.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                    </TableCell>
                    <TableCell sx={{
                      color: result.document_value ? 'text.primary' : 'text.disabled',
                      fontFamily: result.document_value ? 'monospace' : 'inherit',
                    }}>
                      {result.document_value || '—  Not found'}
                    </TableCell>
                    <TableCell sx={{
                      color: result.erp_value ? 'text.primary' : 'text.disabled',
                      fontFamily: result.erp_value ? 'monospace' : 'inherit',
                    }}>
                      {result.erp_value || '—  Not available'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isNoData ? (
                        <Chip
                          label="⚠ No Data"
                          size="small"
                          sx={{
                            backgroundColor: alpha('#6B7280', 0.15),
                            color: '#9CA3AF',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : isMatch ? (
                        <Chip
                          label="✓ Match"
                          size="small"
                          sx={{
                            backgroundColor: alpha('#10B981', 0.15),
                            color: '#10B981',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : (
                        <Chip
                          label="✗ Mismatch"
                          size="small"
                          sx={{
                            backgroundColor: alpha('#EF4444', 0.15),
                            color: '#EF4444',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isMatch ? (
                        <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>✓</Typography>
                      ) : isNoData ? (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      ) : result.discrepancy_type ? (
                        <Chip
                          label={result.discrepancy_type.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            borderColor: alpha('#F59E0B', 0.6),
                            color: '#F59E0B',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {classification && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Classification
          </Typography>
          <Alert severity={classification.is_exception ? 'warning' : 'success'}>
            <Typography variant="subtitle1" gutterBottom>
              {classification.is_exception ? 'Exception Case' : 'Standard Case'}
            </Typography>
            {classification.exception_reasons.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" component="div">
                  <strong>Exception Reasons:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    {classification.exception_reasons.map((reason, idx) => (
                      <li key={idx}>{reason}</li>
                    ))}
                  </ul>
                </Typography>
              </Box>
            )}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUpload;
